import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { loginPlatform } from '@/api/platformApi'
import { clearPlatformToken } from '@/utils/platformAdminAuth'
import { consumeAuthNotice } from '@/utils/authFlow'
import { getPlatformDashboardPath, PLATFORM_ROOT_CLASS } from '@/config/platform'
import { PlatformThemeToggle } from '@/pages/Platform/PlatformThemeToggle'
import './platformTheme.css'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Platform.module.css'

export function PlatformLoginPage() {
  const navigate = useNavigate()
  const [user, setUser] = useState('')
  const [token, setToken] = useState('')
  const [erro, setErro] = useState('')
  const [loading, setLoading] = useState(false)
  const { notifySaved, notifyError, notifyValidation } = useSaveFeedback()

  useEffect(() => {
    const msg = consumeAuthNotice('platform')
    if (msg) setErro(msg)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErro('')
    const u = user.trim()
    const t = token.trim()
    if (!u || !t) {
      const msg = 'Preencha o identificador e a chave de acesso.'
      setErro(msg)
      notifyValidation(msg)
      return
    }
    if (t.length < 24) {
      const msg = 'Chave de acesso incompleta.'
      setErro(msg)
      notifyValidation(msg)
      return
    }
    setLoading(true)
    try {
      await loginPlatform(t, u)
      notifySaved('Sessão da plataforma iniciada.')
      navigate(getPlatformDashboardPath(), { replace: true })
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Credenciais inválidas.'
      const msg =
        raw === 'Rota não encontrada'
          ? 'A API em execução não tem o painel da plataforma. No servidor: git pull, pm2 restart agendamento-api.'
          : raw
      setErro(msg)
      notifyError(err, msg)
      clearPlatformToken()
      setToken('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`${PLATFORM_ROOT_CLASS} ${styles.platformShell}`}>
      <main className={styles.platLoginPage}>
        <div className={styles.platLoginMesh} aria-hidden />
        <div className={styles.platLoginThemeBar}>
          <PlatformThemeToggle compact />
        </div>

        <div className={styles.platLoginCard}>
          <div className={styles.platLoginAccentBar} aria-hidden />
          <div className={styles.platLoginCardInner}>
            <div className={styles.platLoginBrand}>
              <span className={styles.platLoginLogo} aria-hidden>
                G
              </span>
              <div>
                <p className={styles.platLoginBadge}>Área interna · Sol Online</p>
                <h1 className={styles.platLoginTitle}>GoBooking Ops</h1>
                <p className={styles.platLoginSub}>
                  Acesso restrito ao super-admin. Sessão encriptada, cookie httpOnly e validade de 4 horas.
                </p>
              </div>
            </div>

            <ul className={styles.platLoginChecklist}>
              <li>Não partilhe estas credenciais</li>
              <li>Use apenas em redes de confiança</li>
              <li>Termine a sessão ao sair do painel</li>
            </ul>

            {erro ? (
              <p className={styles.erro} role="alert" style={{ marginBottom: 14 }}>
                {erro}
              </p>
            ) : null}

            <form className={styles.platLoginForm} onSubmit={handleSubmit} autoComplete="off">
              <div className={styles.platField}>
                <label className={styles.platLabel} htmlFor="plat-user">
                  Identificador do operador
                </label>
                <input
                  id="plat-user"
                  name="platform-user"
                  type="text"
                  className={styles.platInput}
                  autoComplete="username"
                  spellCheck={false}
                  value={user}
                  onChange={(e) => setUser(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className={styles.platField}>
                <label className={styles.platLabel} htmlFor="plat-token">
                  Chave de acesso
                </label>
                <input
                  id="plat-token"
                  name="platform-token"
                  type="password"
                  className={styles.platInput}
                  autoComplete="current-password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  disabled={loading}
                />
              </div>
              <button type="submit" className={styles.btnGradient} disabled={loading}>
                {loading ? 'A validar…' : 'Entrar no painel'}
              </button>
            </form>
          </div>
          <div className={styles.platLoginCardInner} style={{ paddingTop: 8 }}>
            <button type="button" className={styles.linkGhost} onClick={() => navigate('/')}>
              ← Voltar ao site público
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}
